import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";

function Hero() {
  const [show, setShow] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
      setShow(false);
    }
  };

  return (
    <>
      {/* Navigation Bar */}
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled
            ? "bg-white/95 backdrop-blur-md shadow-lg py-3"
            : "bg-transparent py-6"
        }`}
      >
        <div className="container mx-auto px-6 lg:px-12">
          {/* Desktop Navigation */}
          <div className="hidden lg:flex justify-between items-center">
            <div className="flex items-center space-x-4">
              <img
                className="w-14 h-14 rounded-full shadow-md"
                src="https://aura-yog-centre.vercel.app/static/media/logo.5b184c478a6cdb86763e.png"
                alt="Aura Yog Centre Logo"
              />
              <h1 className="text-2xl font-bold gradient-text">
                AURA YOG CENTRE
              </h1>
            </div>
            <ul className="flex space-x-8 items-center">
              <li>
                <button
                  onClick={() => scrollToSection("home")}
                  className="text-gray-700 hover:text-sage-600 font-semibold transition-colors duration-300 border-b-2 border-sage-600"
                >
                  Home
                </button>
              </li>
              <li>
                <button
                  onClick={() => scrollToSection("services")}
                  className="text-gray-700 hover:text-sage-600 font-semibold transition-colors duration-300 border-b-2 border-transparent hover:border-sage-600"
                >
                  Services
                </button>
              </li>
              <li>
                <button
                  onClick={() => scrollToSection("why-us")}
                  className="text-gray-700 hover:text-sage-600 font-semibold transition-colors duration-300 border-b-2 border-transparent hover:border-sage-600"
                >
                  Why Us
                </button>
              </li>
              <li>
                <button
                  onClick={() => scrollToSection("testimonials")}
                  className="text-gray-700 hover:text-sage-600 font-semibold transition-colors duration-300 border-b-2 border-transparent hover:border-sage-600"
                >
                  Testimonials
                </button>
              </li>
              <li>
                <button
                  onClick={() => scrollToSection("contact")}
                  className="btn-primary text-sm"
                >
                  Contact Us
                </button>
              </li>
            </ul>
          </div>

          {/* Mobile Navigation */}
          <div className="lg:hidden flex justify-between items-center">
            <div className="flex items-center space-x-3">
              <img
                className="w-12 h-12 rounded-full shadow-md"
                src="https://aura-yog-centre.vercel.app/static/media/logo.5b184c478a6cdb86763e.png"
                alt="Aura Yog Centre Logo"
              />
              <h1 className="text-lg font-bold gradient-text">
                AURA YOG CENTRE
              </h1>
            </div>
            <button
              onClick={() => setShow(!show)}
              className="p-2 rounded-lg hover:bg-sage-100 transition-colors"
            >
              {show ? (
                <svg
                  className="w-6 h-6 text-gray-700"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              ) : (
                <svg
                  className="w-6 h-6 text-gray-700"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 6h16M4 12h16M4 18h16"
                  />
                </svg>
              )}
            </button>
          </div>

          {/* Mobile Menu */}
          {show && (
            <motion.ul
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              className="lg:hidden mt-4 bg-white rounded-2xl shadow-xl p-6 space-y-4"
            >
              <li>
                <button
                  onClick={() => scrollToSection("home")}
                  className="block w-full text-left px-4 py-3 rounded-lg hover:bg-sage-50 text-gray-700 font-semibold transition-colors"
                >
                  Home
                </button>
              </li>
              <li>
                <button
                  onClick={() => scrollToSection("services")}
                  className="block w-full text-left px-4 py-3 rounded-lg hover:bg-sage-50 text-gray-700 font-semibold transition-colors"
                >
                  Services
                </button>
              </li>
              <li>
                <button
                  onClick={() => scrollToSection("why-us")}
                  className="block w-full text-left px-4 py-3 rounded-lg hover:bg-sage-50 text-gray-700 font-semibold transition-colors"
                >
                  Why Us
                </button>
              </li>
              <li>
                <button
                  onClick={() => scrollToSection("testimonials")}
                  className="block w-full text-left px-4 py-3 rounded-lg hover:bg-sage-50 text-gray-700 font-semibold transition-colors"
                >
                  Testimonials
                </button>
              </li>
              <li>
                <button
                  onClick={() => scrollToSection("contact")}
                  className="block w-full text-left px-4 py-3 rounded-lg bg-gradient-to-r from-sage-600 to-teal-600 text-white font-semibold transition-colors"
                >
                  Contact Us
                </button>
              </li>
            </motion.ul>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="relative min-h-screen flex items-center overflow-hidden pt-24 lg:pt-0">
        {/* Decorative Blobs */}
        <div className="decoration-blob w-96 h-96 bg-sage-200 top-20 -left-48"></div>
        <div className="decoration-blob w-96 h-96 bg-earth-200 bottom-20 -right-48"></div>

        <div className="container mx-auto px-6 lg:px-12">
          <div className="flex flex-col-reverse lg:flex-row items-center gap-12 lg:gap-20">
            {/* Text Content */}
            <motion.div
              initial={{ opacity: 0, x: -70 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{
                duration: 0.8,
                delay: 0.3,
                ease: "easeInOut",
              }}
              className="flex-1 text-center lg:text-left"
            >
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className="inline-block mb-4 px-4 py-2 bg-sage-100 rounded-full"
              >
                <span className="text-sage-700 font-semibold text-sm">
                  ✨ Welcome to Your Wellness Journey
                </span>
              </motion.div>

              <h1 className="text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold leading-tight mb-6">
                Discover{" "}
                <span className="gradient-text">Serenity Within</span>
              </h1>

              <p className="text-lg md:text-xl text-gray-600 mb-8 leading-relaxed max-w-2xl mx-auto lg:mx-0">
                Your journey to balance, strength, and inner peace begins here.
                Experience authentic yoga practices designed to promote health,
                well-being, and freedom from stress.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <button
                  onClick={() => scrollToSection("services")}
                  className="btn-primary"
                >
                  Explore Services
                </button>
                <button
                  onClick={() => scrollToSection("contact")}
                  className="btn-secondary"
                >
                  Book a Session
                </button>
              </div>

              {/* Stats */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.8 }}
                className="mt-12 grid grid-cols-3 gap-6 max-w-xl mx-auto lg:mx-0"
              >
                <div className="text-center lg:text-left">
                  <div className="text-3xl font-bold gradient-text">100+</div>
                  <div className="text-sm text-gray-600 mt-1">Happy Members</div>
                </div>
                <div className="text-center lg:text-left">
                  <div className="text-3xl font-bold gradient-text">8+</div>
                  <div className="text-sm text-gray-600 mt-1">Services</div>
                </div>
                <div className="text-center lg:text-left">
                  <div className="text-3xl font-bold gradient-text">5⭐</div>
                  <div className="text-sm text-gray-600 mt-1">Rating</div>
                </div>
              </motion.div>
            </motion.div>

            {/* Image Content */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{
                duration: 0.8,
                delay: 0.5,
                ease: "easeInOut",
              }}
              className="flex-1 relative"
            >
              <div className="relative w-full max-w-lg mx-auto">
                {/* Main Image */}
                <div className="relative z-10 rounded-3xl overflow-hidden shadow-2xl">
                  <img
                    src="heroImg.png"
                    alt="Yoga Practice"
                    className="w-full h-auto object-cover animate-float"
                  />
                </div>

                {/* Decorative Elements */}
                <div className="absolute -top-6 -right-6 w-32 h-32 bg-gradient-to-br from-sage-300 to-teal-300 rounded-full blur-2xl opacity-50 animate-pulse"></div>
                <div className="absolute -bottom-6 -left-6 w-32 h-32 bg-gradient-to-br from-earth-300 to-lavender-300 rounded-full blur-2xl opacity-50 animate-pulse"></div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5 }}
          className="absolute bottom-8 left-1/2 transform -translate-x-1/2 hidden lg:block"
        >
          <div className="flex flex-col items-center gap-2 text-gray-400">
            <span className="text-sm font-medium">Scroll to explore</span>
            <motion.div
              animate={{ y: [0, 10, 0] }}
              transition={{ duration: 1.5, repeat: Infinity }}
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 14l-7 7m0 0l-7-7m7 7V3"
                />
              </svg>
            </motion.div>
          </div>
        </motion.div>
      </section>
    </>
  );
}

export default Hero;
